/**
 * Job View Model Mapper
 * Transforms ProviderJobDto to JobCardViewModel
 *
 * Following DDD Principles:
 * - Pure transformation (DTO → ViewModel)
 * - All formatting and UI calculations
 * - No business logic
 */

import type { ProviderJobDto } from '@/types/provider-job';
import type { JobCardViewModel } from '../view-models/JobListViewModel';

/**
 * Map ProviderJobDto to JobCardViewModel
 */
export function mapToViewModel(dto: ProviderJobDto): JobCardViewModel {
  return {
    // Identity
    id: dto.id,
    displayName: formatDisplayName(dto),
    displayAddress: formatAddress(dto.location),

    // Schedule
    startDateTimestamp: new Date(dto.schedule.scheduledDate).getTime() / 1000, // Convert to Unix seconds
    targetCompletionDateTimestamp: undefined, // Not in new schema
    scheduleText: formatScheduleText(dto.schedule),
    priority: dto.schedule.priority,
    timeWindow: dto.schedule.timeWindow,

    // Status
    status: dto.status,
    statusBadge: getStatusBadge(dto.status),

    // Service details
    serviceType: formatServiceType(dto.serviceType),
    containerSize: dto.containerSize,
    materialType: dto.materialType,
    estimatedWaste: undefined, // TODO: Calculate from pricing or add to DTO

    // Resource assignments
    assignedContainer: dto.assignedContainer
      ? {
          id: dto.assignedContainer.containerId,
          containerNumber: dto.assignedContainer.containerNumber,
          displayName: `${dto.containerSize} #${dto.assignedContainer.containerNumber}`,
        }
      : undefined,
    assignedVehicle: dto.assignedVehicle
      ? {
          id: dto.assignedVehicle.vehicleId,
          vehicleNumber: dto.assignedVehicle.vehicleNumber,
          displayName: `Vehicle #${dto.assignedVehicle.vehicleNumber}`,
        }
      : undefined,
    assignedDriver: dto.assignedDriver
      ? {
          id: dto.assignedDriver.driverId,
          name: dto.assignedDriver.driverName,
        }
      : undefined,

    // Source
    planId: dto.orderId, // Order ID can indicate marketplace source
    source: dto.orderId ? 'marketplace' : 'direct',
    bidAmount: dto.pricing?.total,

    // Capabilities
    canEdit: dto.status === 'scheduled',
    canStart: dto.status === 'scheduled' && hasRequiredResources(dto),
    canComplete: dto.status === 'in_progress',
    canCancel: dto.status === 'scheduled' || dto.status === 'in_progress',
    canViewDetails: true,

    // Activity
    lastUpdatedBy: undefined, // Not in current DTO
    lastUpdatedAt: new Date(dto.updatedAt).getTime() / 1000,

    // Urgency
    urgencyLevel: calculateUrgency(dto),
    daysRemaining: calculateDaysRemaining(dto.schedule.scheduledDate) ?? undefined,

    // Legacy fields for backwards compatibility
    projectType: formatServiceType(dto.serviceType),
    targetDiversionRate: undefined,
    startDate: formatDate(dto.schedule.scheduledDate),
    targetCompletionDate: undefined,
    urgencyColor: getUrgencyColor(calculateUrgency(dto)),
  };
}

/**
 * Map array of DTOs to ViewModels
 */
export function mapToViewModelList(dtos: ProviderJobDto[]): JobCardViewModel[] {
  return dtos.map(mapToViewModel);
}

// Helper Functions

function formatDisplayName(dto: ProviderJobDto): string {
  // Use customer name and service type for display
  const serviceType = formatServiceType(dto.serviceType);
  return `${serviceType} - ${dto.customer.name}`;
}

function formatAddress(location: ProviderJobDto['location']): string {
  return `${location.address}, ${location.city}, ${location.state} ${location.zipCode}`;
}

function formatScheduleText(schedule: ProviderJobDto['schedule']): string {
  const date = new Date(schedule.scheduledDate);
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const timeWindowMap = {
    morning: 'Morning (8AM-12PM)',
    afternoon: 'Afternoon (12PM-5PM)',
    evening: 'Evening (5PM-8PM)',
    anytime: 'Anytime',
  };

  const timeWindow = schedule.timeWindow
    ? timeWindowMap[schedule.timeWindow]
    : 'Anytime';

  return `${dateStr} - ${timeWindow}`;
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatServiceType(serviceType: string): string {
  const typeMap: Record<string, string> = {
    delivery: 'Delivery',
    pickup: 'Pickup',
    swap: 'Swap',
    permanent_placement: 'Permanent Placement',
  };
  return typeMap[serviceType] || serviceType;
}

function getStatusBadge(status: string): {
  label: string;
  color: string;
  icon: string;
} {
  const statusMap: Record<
    string,
    { label: string; color: string; icon: string }
  > = {
    scheduled: { label: 'Scheduled', color: 'blue', icon: 'calendar' },
    in_progress: { label: 'In Progress', color: 'yellow', icon: 'truck' },
    completed: { label: 'Completed', color: 'green', icon: 'check' },
    cancelled: { label: 'Cancelled', color: 'red', icon: 'x' },
  };

  return (
    statusMap[status] || {
      label: status.charAt(0).toUpperCase() + status.slice(1),
      color: 'gray',
      icon: 'info',
    }
  );
}

function hasRequiredResources(dto: ProviderJobDto): boolean {
  // For service types that require containers, check if assigned
  const requiresContainer = ['delivery', 'pickup', 'swap'].includes(
    dto.serviceType
  );

  if (requiresContainer && !dto.assignedContainer) {
    return false;
  }

  // Optionally check for vehicle and driver
  // For now, assume they're optional
  return true;
}

function calculateUrgency(dto: ProviderJobDto): 'normal' | 'soon' | 'urgent' {
  const daysRemaining = calculateDaysRemaining(dto.schedule.scheduledDate);

  if (daysRemaining === null) {
    return 'normal';
  }

  if (daysRemaining < 0) {
    return 'urgent'; // Overdue
  } else if (daysRemaining <= 2) {
    return 'soon'; // Due very soon
  } else {
    return 'normal';
  }
}

function calculateDaysRemaining(scheduledDate: string): number | null {
  try {
    const target = new Date(scheduledDate);
    const now = new Date();
    const diffMs = target.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

function getUrgencyColor(urgency: 'normal' | 'soon' | 'urgent'): string {
  const colors = {
    normal: 'green',
    soon: 'yellow',
    urgent: 'red',
  };
  return colors[urgency];
}
